import { CartLinesUpdateEvent } from '@shopify/events';

if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.tier1Qty = parseInt(this.dataset.tier1Qty, 10) || 2;
      this.tier1Pct = parseInt(this.dataset.tier1Pct, 10) || 10;
      this.tier2Qty = parseInt(this.dataset.tier2Qty, 10) || 4;
      this.tier2Pct = parseInt(this.dataset.tier2Pct, 10) || 15;
      this.initialVisible = parseInt(this.dataset.initialVisible, 10) || 10;

      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      this.toggles = Array.from(this.querySelectorAll('[data-bundle-toggle]'));
      this.tabs = Array.from(this.querySelectorAll('[data-bundle-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-bundle-panel]'));
      this.tabCounts = Array.from(this.querySelectorAll('[data-bundle-tab-count]'));
      this.infoOpenButtons = Array.from(this.querySelectorAll('[data-bundle-info-open]'));
      this.infoCloseButtons = Array.from(this.querySelectorAll('[data-bundle-info-close]'));
      this.infoModals = Array.from(this.querySelectorAll('[data-bundle-info-modal]'));
      this.fill = this.querySelector('[data-bundle-fill]');
      this.text = this.querySelector('[data-bundle-text]');
      this.saving = this.querySelector('[data-bundle-saving]');
      this.mobileSaving = this.querySelector('[data-bundle-saving-mobile]');
      this.bar = this.querySelector('[data-bundle-bar]');
      this.addButton = this.querySelector('[data-bundle-add]');
      this.addButtonLabels = Array.from(this.querySelectorAll('[data-bundle-add-label]'));
      this.viewMoreButton = this.querySelector('[data-bundle-view-more]');
      this.activePhase = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.bundleTab;
      this.activeInfoModal = null;

      this.toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => this.toggleSelection(toggle));
      });

      this.infoOpenButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openInfoModal(button);
        });
      });

      this.infoCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeInfoModal());
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeInfoModal();
      });

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.bundleTab));
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addSelectionToCart());
      }

      if (this.viewMoreButton) {
        this.viewMoreButton.addEventListener('click', () => this.showAll());
      }

      this.applyVisibleLimit();
      this.updateTabCounts();
      this.updateSummary();
    }

    openInfoModal(button) {
      const target = button.dataset.bundleInfoTarget;
      const modal = this.infoModals.find((infoModal) => infoModal.dataset.bundleInfoModal === target);
      if (!modal) return;

      if (this.activeInfoModal && this.activeInfoModal !== modal) {
        this.activeInfoModal.hidden = true;
      }

      modal.hidden = false;
      this.activeInfoModal = modal;

      const closeButton = modal.querySelector('.bundle-builder__info-close');
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    closeInfoModal() {
      if (!this.activeInfoModal) return;

      const modal = this.activeInfoModal;
      const opener = this.infoOpenButtons.find((button) => button.dataset.bundleInfoTarget === modal.dataset.bundleInfoModal);

      modal.hidden = true;
      this.activeInfoModal = null;

      if (opener) opener.focus({ preventScroll: true });
    }

    getActivePanel() {
      return this.panels.find((panel) => panel.dataset.bundlePanel === this.activePhase) || this.panels[0];
    }

    getActiveGrid() {
      return this.getActivePanel()?.querySelector('.bundle-builder__grid');
    }

    getActiveCards() {
      return Array.from(this.getActivePanel()?.querySelectorAll('[data-bundle-card]') || []);
    }

    selectTab(phase) {
      if (!phase || phase === this.activePhase) return;

      this.activePhase = phase;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.bundleTab === phase;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.bundlePanel !== phase;
      });

      this.applyVisibleLimit();
    }

    applyVisibleLimit() {
      const activeGrid = this.getActiveGrid();
      const activeCards = this.getActiveCards();
      const isExpanded = activeGrid?.classList.contains('is-expanded');

      activeCards.forEach((card, index) => {
        card.hidden = !isExpanded && index >= this.initialVisible;
      });

      if (this.viewMoreButton) {
        const shouldShow = activeCards.length > this.initialVisible && !isExpanded;
        this.viewMoreButton.hidden = !shouldShow;
      }
    }

    showAll() {
      const activeGrid = this.getActiveGrid();
      if (activeGrid) activeGrid.classList.add('is-expanded');
      this.applyVisibleLimit();
    }

    toggleSelection(toggle) {
      const selected = toggle.dataset.selected === 'true';
      toggle.dataset.selected = selected ? 'false' : 'true';

      const card = toggle.closest('[data-bundle-card]');
      if (card) card.classList.toggle('is-selected', !selected);

      const label = toggle.querySelector('[data-bundle-toggle-label]');
      if (label) label.textContent = selected ? 'Aggiungi' : 'Aggiunto';

      this.updateTabCounts();
      this.updateSummary();
    }

    getSelected() {
      return this.toggles.filter((toggle) => toggle.dataset.selected === 'true');
    }

    updateSummary() {
      const selected = this.getSelected();
      const count = selected.length;

      let message = this.dataset.emptyText || `Scegli almeno ${this.tier1Qty} prodotti per sbloccare uno sconto`;
      let saving = `Sconto fino al ${this.tier2Pct}%`;
      let savingState = 'idle';

      if (count >= this.tier2Qty) {
        message = `${count} prodotti selezionati — traguardo massimo raggiunto.`;
        saving = `✓ ${this.tier2Pct}% sbloccato`;
        savingState = 'tier2';
      } else if (count >= this.tier1Qty) {
        const missing = this.tier2Qty - count;
        message = `${count} prodotti selezionati — aggiungine ${missing} per arrivare al ${this.tier2Pct}%.`;
        saving = `✓ ${this.tier1Pct}% sbloccato`;
        savingState = 'tier1';
      } else if (count > 0) {
        const missing = this.tier1Qty - count;
        message = `${count} prodott${count === 1 ? 'o' : 'i'} selezionat${count === 1 ? 'o' : 'i'} — aggiungine ${missing} per sbloccare il ${this.tier1Pct}%.`;
        saving = `+ ${this.tier1Pct}% quasi pronto`;
        savingState = 'near';
      }

      const fillTarget = Math.max(this.tier1Qty, this.tier2Qty);
      const fillPercent = Math.min(100, Math.round((count / fillTarget) * 100));

      if (this.fill) this.fill.style.width = `${fillPercent}%`;
      if (this.text) this.text.textContent = message;
      if (this.saving) this.saving.textContent = saving;
      if (this.mobileSaving) this.mobileSaving.textContent = saving;
      if (this.bar) this.bar.dataset.bundleSavingState = savingState;
      if (this.addButton) this.addButton.disabled = count < this.tier1Qty;
    }

    setAddButtonLabel(label) {
      this.addButtonLabels.forEach((labelElement) => {
        labelElement.textContent = label;
      });
    }

    resetAddButtonLabel() {
      this.addButtonLabels.forEach((labelElement) => {
        labelElement.textContent = labelElement.classList.contains('bundle-builder__cta-label--mobile')
          ? 'Aggiungi al Carrello'
          : this.dataset.buttonLabel || 'Aggiungi bundle al carrello';
      });
    }

    updateTabCounts() {
      this.tabCounts.forEach((counter) => {
        const phase = counter.dataset.bundleTabCount;
        const count = this.cards.filter(
          (card) =>
            card.dataset.bundlePhase === phase &&
            card.querySelector('[data-bundle-toggle]')?.dataset.selected === 'true'
        ).length;

        counter.textContent = count > 0 ? count : '';
        counter.setAttribute('aria-label', count > 0 ? `${count} prodotti selezionati` : '');
      });
    }

    async refreshCart() {
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        return cartItemsComponent.fetchCartData();
      }
      return fetch('/cart.json', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());
    }

    async addSelectionToCart() {
      const selected = this.getSelected();
      if (selected.length < this.tier1Qty) return;

      const items = selected.map((toggle) => ({
        id: parseInt(toggle.dataset.variantId, 10),
        quantity: 1,
      }));

      this.addButton.disabled = true;
      this.setAddButtonLabel('Aggiungo...');

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
          promise: deferredEventPromise.promise,
        })
      );

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error('cart add failed');

        const ajaxCart = await this.refreshCart();

        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'bundle-builder',
            sourceId: 'bundle-builder',
            itemCount: items.length,
            didError: false,
          },
        });

        this.setAddButtonLabel('Aggiunto!');
        setTimeout(() => {
          this.resetAddButtonLabel();
          this.updateSummary();
        }, 1800);
      } catch (error) {
        deferredEventPromise.reject(error);
        this.setAddButtonLabel('Errore, riprova');
        setTimeout(() => {
          this.resetAddButtonLabel();
          this.updateSummary();
        }, 2000);
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
